import { html, LitElement, nothing, type PropertyValueMap, unsafeCSS } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { map } from 'lit/directives/map.js';
import { range } from 'lit/directives/range.js';
import { repeat } from 'lit/directives/repeat.js';
import type { FAQ } from '../../../app/package/base/cms/package/cms-domain';
import { LoginStatus } from '../../../app/package/base/service/login/login-domain';
import {
  getGameType,
  TrackableEventAction,
  TrackingEventSource,
} from '../../../app/package/base/service/tracking/tracking-service';
import {
  FetchPriority,
  type GameContent,
  type LobbyGame,
  TileType,
} from '../../base/package/api/package/gaming/gaming-domain';
import { I18n } from '../../base/package/services/package/i18n/i18n.de';
import baseThemeStyle from '../../base/package/themes/de-games/base-theme.css?inline';
import { handleKeyboardTrigger } from '../../base/package/util/a11y';
import {
  addLdJsonFAQ,
  appendOpenGraph,
  createCanonicalUrl,
  removeLdJsonFAQ,
  unloadOpenGraph,
} from '../../base/package/util/html-helper';
import { formatMoney } from '../../base/package/util/money';
import '../carousels/ui-games-carousel';
import '../ui-accordion-item/ui-accordion-item';
import '../ui-btn/ui-btn';
import '../ui-icons/ui-chevron-arrow-icon';
import '../ui-modal/ui-modal';
import '../ui-placeholder/ui-placeholder';
import '../ui-rich-text/ui-rich-text';
import type { UIModal } from '../ui-modal/ui-modal';
import '../ui-tag/ui-tag';
import styles from './ui-game-info.css?inline';
import '../ui-volatility-rating/ui-volatility-rating';

const CName = 'ui-game-info';

@customElement(CName)
export class UIGameInfo extends LitElement {
  static readonly styles = [unsafeCSS(styles), unsafeCSS(baseThemeStyle)];

  @property({ type: Object }) game: GameContent;
  @property({ type: Array }) faq: FAQ[];
  @property({ type: Array }) similarGames: LobbyGame[] | undefined;
  @property({ type: Boolean }) maintenance = false;

  @state() private _loggedIn = false;
  @state() private _isVolatilityInfoShown = false;

  @query('#canvas') private readonly _canvas?: HTMLCanvasElement;
  @query('#banner') private readonly _banner?: HTMLImageElement | HTMLVideoElement;

  private readonly _ogDataSet = 'gameinfo';

  connectedCallback() {
    super.connectedCallback();

    window.$app.login.getFinalLoginStatus().then((status) => {
      this._loggedIn = status === LoginStatus.LOGGED_IN;
    });

    if (this.faq?.[0]?.content && this.faq[0]?.title) {
      addLdJsonFAQ(this.faq);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    unloadOpenGraph(this._ogDataSet);
    if (this.faq?.[0]?.content && this.faq[0]?.title) {
      removeLdJsonFAQ();
    }
  }

  updated(props: PropertyValueMap<any> | Map<PropertyKey, unknown>) {
    if (props.has('game')) {
      unloadOpenGraph(this._ogDataSet);
      this._updateBannerSource();
      this._renderOpenGraph();
      this._trackGamesImpression();
    }
  }

  render() {
    return html`
      <div class="game-info">
        <div class="info">
          <div class="graphics">
            <div class="banner__wrapper">
              <div class=${`banner ${this.maintenance ? 'banner--maintenance' : ''}`} @click=${this._handleBannerClick}>
                <canvas id="canvas"></canvas>
                ${this._renderBanner()}
              </div>
            </div>
            <div class="provider">
              <div class="provider-item">
                <span>${window.$app.$t.get(I18n.gameInfo.studio)}</span>
                <a
                  href=${this._getPathNavigateToStudio()}
                  @click=${(ev: PointerEvent) => ev.preventDefault()}
                  class="seo-link"
                  @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._navigateToStudio.bind(this))}
                >
                  <div class="provider-link" @click=${this._navigateToStudio}>
                    <span class="provider-text" lang="en">${this.game.studio}</span>
                    <ui-chevron-arrow-icon></ui-chevron-arrow-icon>
                  </div>
                </a>
              </div>
              ${this._renderVolatility()}
            </div>
            ${this._renderTags()} ${this._renderButtons()}
          </div>
        </div>
        ${this._renderStats()} ${this._renderSimilar()} ${this._renderDescription()} ${this._renderFAQ()}
      </div>
      ${this._isVolatilityInfoShown ? this._renderVolatilityDrawer() : nothing}
    `;
  }

  private _renderDescription() {
    if (this.game.description) {
      return html`
        <div class="description">
          <ui-rich-text class="richText" .content=${this.game.description}></ui-rich-text>
        </div>
      `;
    }

    return nothing;
  }

  private _renderButtons() {
    if (this.maintenance) {
      return html`<ui-btn .isBlock=${true} .isDisabled=${true} variant="secondary">
        ${window.$app.$t.get(I18n.maintenance.gameTile)}
      </ui-btn>`;
    }
    if (!this._loggedIn) {
      return html`
        <ui-btn .isBlock=${true} variant="secondary" @click=${this._handleLoginAndPlay}>
          ${window.$app.$t.get(I18n.base.loginAndPlay)}
        </ui-btn>
      `;
    }
    return html`
      <div class="play-action">
        <ui-btn
          .isBlock=${true}
          variant="secondary"
          @click=${() => {
            this._trackGameClick(this.game, TrackableEventAction.GAME_SELECTED_INFO_PAGE);
            window.$app.router.navigateToGame(true, this.game.id);
          }}
        >
          ${window.$app.$t.get(I18n.base.playNow)}
        </ui-btn>
        ${this._renderDemoButton()}
      </div>
    `;
  }

  private _renderDemoButton() {
    return this.game.hasFunMode
      ? html`
          <ui-btn @click=${() => window.$app.router.navigateToGame(false, this.game.id)} variant="secondary-outlined" .isBlock=${true}>
            ${window.$app.$t.get(I18n.base.demo)}
          </ui-btn>
        `
      : nothing;
  }

  private _renderStatLine<T>(value: T, i18n: string, labelFn?: (value: T) => string) {
    if (!value) {
      return nothing;
    }

    return html`
      <div class="stats-item">
        <span>${window.$app.$t.get(i18n)}</span><span class="stats-value">${labelFn ? labelFn(value) : value}</span>
      </div>
    `;
  }

  private _renderStats() {
    return html`
      <div class="stats">
        ${this._renderStatLine(this.game.numWinlines, I18n.gameInfo.winlines)}
        ${this._renderStatLine(this.game.numReels, I18n.gameInfo.reels)}
        ${this._renderStatLine(
          [this.game.minBet, this.game.maxBet],
          I18n.gameInfo.minmax,
          ([minBet, maxBet]) => `${formatMoney(Number(minBet))}/${formatMoney(Number(maxBet))}`,
        )}
        ${this._renderStatLine(this.game.maxPayout, I18n.gameInfo.payout, (v) => `${v}x`)}
        ${this._renderStatLine(this.game.hitFrequency, I18n.gameInfo.hitFrequency)}
        ${this._renderStatLine(this.game.numBetMin, I18n.gameInfo.numBetMin)}
        ${this._renderStatLine(this.game.numBetMax, I18n.gameInfo.numBetMax)}
        ${this._renderStatLine(
          this.game.maxWin,
          I18n.gameInfo.maxWin,
          (v) => `${new Intl.NumberFormat('de-DE').format(Number(v))}x`,
        )}
      </div>
    `;
  }

  private _renderTag(name: string, slug: string) {
    return html`
      <div role="listitem">
        <a
          href=${window.$app.router.getPathNavigateToTag(slug)}
          class="seo-link"
          @click=${(ev: PointerEvent) => ev.preventDefault()}
          @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._handleTagClick.bind(this, slug))}
        >
          <ui-tag @click=${() => this._handleTagClick(slug)} tabindex="-1">${name}</ui-tag>
        </a>
      </div>
    `;
  }

  private _renderTags() {
    if (!this.game.tags || this.game.tags.length === 0) {
      return nothing;
    }
    return html`
      <div class="tags" role="list">
        ${repeat(
          this.game.tags,
          (_, key) => key,
          (tag) => this._renderTag(tag.name, tag.slug),
        )}
      </div>
    `;
  }

  private _renderBanner() {
    // We can hardcode the video type to `video/mp4` as all videos will be mp4 containers
    return this.game.vid
      ? html`
          <video
            autoplay
            playsinline
            muted
            disablePictureInPicture
            disableRemotePlayback
            id="banner"
            @loadeddata=${this._drawVideoCanvas}
            loop
            poster="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
            aria-label=${window.$app.$t.get(I18n.a11y.video.gameTile, { name: this.game.title })}
            crossorigin="anonymous"
            preload="auto"
          >
            <source src="${this.game.vid}" type="video/mp4" />
            <p lang="${window.$app.appConfig.lang}">${window.$app.$t.get(I18n.a11y.video.gameTile, { name: this.game.title })}</p>
          </video>
        `
      : html`<img id="banner" src="${this.game.img}" alt=${window.$app.$t.get(I18n.a11y.image.coverArtFor, { name: this.game.title })} @load=${this._drawImageCanvas} />`;
  }

  private _renderSimilar() {
    if (this.similarGames === undefined) {
      return html`
        <div class="similar">
          <h2 class="title">${window.$app.$t.get(I18n.base.similar, { name: this.game.title })}</h2>
          ${this._renderPlaceholder()}
        </div>
      `;
    }

    const hasSimilar = this.similarGames?.length > 0;
    if (!hasSimilar) {
      return nothing;
    }

    return html`
      <div class="similar">
        <h2 class="title">${window.$app.$t.get(I18n.base.similar, { name: this.game.title })}</h2>
        <ui-games-carousel
          class="carousel"
          .trackingSource=${TrackingEventSource.GAME_INFO_PAGE}
          .categorySlug=${`base.similar.${this.game.slug}`}
          .games=${this.similarGames}
          .fetchPriority=${FetchPriority.HIGH}
          padding="var(--page-padding-inline)"
        ></ui-games-carousel>
      </div>
    `;
  }

  private _renderPlaceholder() {
    return html`
      <div class="placeholder ph-tiles">
        ${map(range(11), () => html`<ui-placeholder .type=${TileType.P1}></ui-placeholder>`)}
      </div>
    `;
  }

  private _renderVolatility() {
    if (!this.game.volatility) return nothing;

    return html`
      <div class="provider-item" @click=${() => this._showDiscovery(true)}>
        <span>${window.$app.$t.get(I18n.gameInfo.volatility)}</span>
        <ui-volatility-rating
          class="volatility"
          .value=${this.game.volatility}
          iconSrc=${window.staticAssets.icons?.volatilityIcon ?? ''}
        ></ui-volatility-rating>
      </div>
    `;
  }

  private _renderFAQ() {
    if (!this.faq || (this.faq && !this.faq[0]?.content) || (this.faq && !this.faq[0]?.title)) {
      return nothing;
    }

    if ((this.faq?.length ?? 0) > 0) {
      return html`
        <div class="faq">
          <h2 class="title">${window.$app.$t.get(I18n.base.faq)}</h2>
          <div class="accordion">
            ${repeat(
              this.faq,
              (faq) => faq.id,
              (faq) => html`
                <ui-accordion-item>
                  <div slot="title" class="accordion-title">${faq.title}</div>
                  <div slot="content" class="accordion-content">
                    <ui-rich-text .content=${faq.content}></ui-rich-text>
                  </div>
                </ui-accordion-item>
              `,
            )}
          </div>
        </div>
      `;
    }

    return nothing;
  }

  private async _renderOpenGraph() {
    const metaTitleTemplate = window.$app.appSettings.isSHCasino
      ? I18n.sh.gameInfo.seoMetaTitle
      : I18n.gameInfo.seoMetaTitle;
    appendOpenGraph('title', window.$app.$t.get(metaTitleTemplate, { title: this.game.title }), this._ogDataSet);

    const url = createCanonicalUrl();
    appendOpenGraph('url', url, this._ogDataSet);

    let ogImg: string | undefined;
    if (this.game.img) {
      ogImg = this.game.img;
    } else {
      const gameContent = await window.$app.content.getGame(this.game.id);
      if (gameContent?.img) {
        ogImg = gameContent.img;
      }
    }

    if (ogImg) {
      appendOpenGraph('image', ogImg, this._ogDataSet);
    }
  }

  private _renderVolatilityDrawer() {
    return html`
      <ui-modal .onClosedAction=${() => this._showDiscovery(false)} .onAction=${(e: UIModal) => e.close()}>
        <div slot="title">${window.$app.$t.get(I18n.gameInfo.volatility)}</div>
        <div slot="main">${window.$app.$t.get(I18n.gameInfo.volatilityText)}</div>
      </ui-modal>
    `;
  }

  private _handleTagClick(slug: string) {
    window.$app.trackingStore.next({
      ...window.$app.trackingStore.value,
      gameSource: TrackingEventSource.GAME_INFO_PAGE,
      winType: undefined,
    });
    window.$app.router.navigateToTag(slug);
  }

  private _handleBannerClick() {
    if (this.maintenance) {
      return;
    }
    if (this._loggedIn) {
      this._trackGameClick(
        this.game,
        this.game.vid
          ? TrackableEventAction.GAME_SELECTED_INFO_PAGE_VID
          : TrackableEventAction.GAME_SELECTED_INFO_PAGE_IMG,
      );
      window.$app.router.navigateToGame(true, this.game.id);
    } else {
      this._handleLoginAndPlay();
    }
  }

  private _showDiscovery(show: boolean) {
    this._isVolatilityInfoShown = show;
  }

  private _navigateToStudio() {
    const studio = window.$app.content.getLobby()?.value?.studios?.find((s) => s.name === this.game.studio);
    if (studio) {
      window.$app.router.navigateToProvider(studio.slug);
    }
  }

  private _getPathNavigateToStudio() {
    const studio = window.$app.content.getLobby()?.value?.studios?.find((s) => s.name === this.game.studio);
    if (studio) {
      return window.$app.router.getPathNavigateToProvider(studio.slug);
    }

    return '';
  }

  private _drawImageCanvas() {
    if (!this._canvas || !this._banner) {
      window.$app.logger.error('Canvas or Banner element not found', '_canvas or _banner is undefined');
      return;
    }

    const ctx = this._canvas.getContext('2d');
    if (ctx === null) return;

    // Match canvas internal resolution to its CSS-rendered size
    this._canvas.width = this._canvas.offsetWidth;
    this._canvas.height = this._canvas.offsetHeight;

    // Draw the image scaled to fill the entire canvas (oversized for glow)
    ctx.drawImage(this._banner, 0, 0, this._canvas.width, this._canvas.height);
  }

  private _drawVideoCanvas() {
    if (!this._canvas || !this._banner) {
      window.$app.logger.error('Canvas or Banner element not found', '_canvas or _banner is undefined');
      return;
    }

    const ctx = this._canvas.getContext('2d');
    if (ctx === null) return;

    // Match canvas internal resolution to its CSS-rendered size
    // (CSS makes it wider/taller than the video for the glow overflow)
    this._canvas.width = this._canvas.offsetWidth;
    this._canvas.height = this._canvas.offsetHeight;

    // Draw the video frame scaled to fill the entire canvas
    ctx.drawImage(this._banner, 0, 0, this._canvas.width, this._canvas.height);
  }

  private _handleLoginAndPlay() {
    window.$app.product.gotoLogin(TrackingEventSource.GAME_INFO_PAGE, `/game/real/${this.game.id}`);
  }

  private _updateBannerSource() {
    if (!this._canvas || !this._banner) {
      window.$app.logger.error('Canvas or Banner element not found', '_canvas or _banner is undefined');
      return;
    }

    // Clear canvas
    const ctx = this._canvas.getContext('2d');
    if (ctx !== null) {
      ctx.clearRect(0, 0, this._canvas.width, this._canvas.height);
    }

    if (this.game.vid) {
      (this._banner as HTMLVideoElement).load();
    }
  }

  private async _trackGameClick(gameContent: GameContent, eventAction: TrackableEventAction) {
    const game = await window.$app.content.getGame(gameContent.id);
    const gamePosition = window.$app.trackingStore.value.gamePosition;
    const gameType = getGameType(gameContent.title);
    const gameSource = window.$app.trackingStore.value.gameSource || TrackingEventSource.GAME_INFO_PAGE;
    const trackingParams = {
      gameCategory: game.labels?.join(' - '),
      gameFilters: window.$app.trackingStore.value.gameFilters,
      gameName: gameContent.title,
      gamePosition,
      gamePromo: this.game.vid ? 'video' : 'image',
      gameProvider: gameContent.studio,
      gameSelect: 'gameInfoPG',
      gameSource,
      gameType,
    };

    if (game && gameContent) {
      window.$app.trackingStore.next({ ...window.$app.trackingStore.value, ...trackingParams });
      window.$app.track.gamePlay({
        eventAction,
        ...trackingParams,
        winType: undefined,
      });
    }
  }

  private _trackGamesImpression() {
    window.$app.vaix.trackGamesImpressionVaix([
      {
        location: 'similar',
        gameSlugs: this.similarGames ? this.similarGames.map((game) => game.slug) : [''],
      },
    ]);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UIGameInfo;
  }
}
